import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { OccupancyItem } from "../models/responses";
import { useAppDispatch } from "./store";
import { TypedUseSelectorHook, useSelector } from "react-redux";

export type OccupancySliceState = {
    date: Date;
    selectedApartment: number;
    selectedGroup: number;
    occupancies: OccupancyItem[];
};

interface PartialRootState {
    occupancy: OccupancySliceState;
}

const initialState: OccupancySliceState = {
    date: new Date(),
    occupancies: [],
    selectedApartment: 0,
    selectedGroup: 0,
};

const occupancySlice = createSlice({
    name: "occupancy",
    initialState: initialState,
    reducers: {
        setDate: (state, action: PayloadAction<Date>) => {
            state.date = action.payload;
        },
    },
    selectors: {
        getDate: (state) => state.date,
    },
});

export default occupancySlice;

interface OccupancyActions {
    setDate: (date: Date) => void;
}

export const useOccupancySelector: TypedUseSelectorHook<PartialRootState> = useSelector;

export const useOccupancyActionsDispatch = (): OccupancyActions => {
    const dispatch = useAppDispatch();

    const setDate = (date: Date) => {
        dispatch(occupancySlice.actions.setDate(date));
    };

    return {
        setDate,
    };
};

export const { getDate } = occupancySlice.selectors;

// Memoised "date one year before currentDate". Re-derived only when the
// underlying date reference changes; without this, the selector would
// return a fresh `Date` instance every call and trigger the
// react-redux "Selector wrapper returned a different result" warning,
// causing extra renders.
export const getPreviousYearsDate = createSelector([getDate], (date: Date) => {
    return new Date(date.getFullYear() - 1, date.getMonth(), date.getDate());
});
